import { Action, combineReducers } from "redux";
import { createReducer, IAction } from "reduxsauce";

import { Types as AllTypes } from "../../actions";
import { ICourse, IScormPackage, RequestState } from "../types/common";

export interface IState {
  _: ICoursesState;
}

export interface ICoursesState {
  courses: ICourse[] | null;
  fetchAllCoursesRequestError: string | null;
  fetchAllCoursesRequestState: RequestState;
  fetchAllScormPackagesRequestError: string | null;
  fetchAllScormPackagesRequestState: RequestState;
  createNewCourseRequestError: string | null;
  createNewCourseRequestState: RequestState;
  deleteCourseRequestError: string | null;
  deleteCourseRequestState: RequestState;
  scormPackages: IScormPackage[] | null;
}

const Types = (AllTypes as any)._APP._COURSES;

export const INITIAL_STATE: ICoursesState = {
  courses: null,
  createNewCourseRequestError: null,
  createNewCourseRequestState: RequestState.IDLE,
  deleteCourseRequestError: null,
  deleteCourseRequestState: RequestState.IDLE,
  fetchAllCoursesRequestError: null,
  fetchAllCoursesRequestState: RequestState.IDLE,
  fetchAllScormPackagesRequestError: null,
  fetchAllScormPackagesRequestState: RequestState.IDLE,
  scormPackages: null,
};

export function mount(state: ICoursesState, action: IAction): ICoursesState {
  return INITIAL_STATE;
}

export function unmount(state: ICoursesState, action: IAction): ICoursesState {
  return INITIAL_STATE;
}

export function fetchAllScormPackages(state: ICoursesState, _: IAction): ICoursesState {
  return {
    ...state,
    fetchAllScormPackagesRequestError: null,
    fetchAllScormPackagesRequestState: RequestState.PENDING,
    scormPackages: [],
  };
}

export function fetchAllCourses(state: ICoursesState, action: IAction): ICoursesState {
  return {
    ...state,
    fetchAllCoursesRequestError: null,
    fetchAllCoursesRequestState: RequestState.PENDING,
  };
}

export function handleFetchAllCoursesFailure(state: ICoursesState, action: IAction): ICoursesState {
  return {
    ...state,
    fetchAllCoursesRequestError: action.error.message,
    fetchAllCoursesRequestState: RequestState.FAILED,
  };
}

export function handleFetchAllCoursesSuccess(state: ICoursesState, action: IAction): ICoursesState {
  return {
    ...state,
    courses: action.courses,
    fetchAllCoursesRequestError: null,
    fetchAllCoursesRequestState: RequestState.SUCCEEDED,
  };
}

interface IScormPackagesAction extends IAction {
  scormPackages: IScormPackage[];
}

export function handleFetchAllScormPackagesSuccess(state: ICoursesState, action: IScormPackagesAction): ICoursesState {
  return {
    ...state,
    fetchAllScormPackagesRequestError: null,
    fetchAllScormPackagesRequestState: RequestState.SUCCEEDED,
    scormPackages: action.scormPackages,
  };
}

interface IErrorMsgAction extends IAction {
  error: {
    message: string;
  };
}

export function handleFetchAllScormPackagesFailure(state: ICoursesState, action: IErrorMsgAction): ICoursesState {
  return {
    ...state,
    fetchAllScormPackagesRequestError: action.error.message,
    fetchAllScormPackagesRequestState: RequestState.FAILED,
    scormPackages: null,
  };
}

export const createCourse = (state: ICoursesState, _: IAction): ICoursesState => ({
  ...state,
  createNewCourseRequestError: null,
  createNewCourseRequestState: RequestState.PENDING,
});

export const handleCreateCourseSuccess = (state: ICoursesState): ICoursesState => ({
  ...state,
  createNewCourseRequestError: null,
  createNewCourseRequestState: RequestState.SUCCEEDED,
});

export const handleCreateCourseFailure = (state: ICoursesState, action: IAction): ICoursesState => ({
  ...state,
  createNewCourseRequestError: action.error.message,
  createNewCourseRequestState: RequestState.FAILED,
});

export const deleteCourse = (state: ICoursesState, _: IAction): ICoursesState => ({
  ...state,
  deleteCourseRequestError: null,
  deleteCourseRequestState: RequestState.PENDING,
});

export const handleDeleteCourseFailure = (state: ICoursesState, action: IAction): ICoursesState => ({
  ...state,
  deleteCourseRequestError: action.error.message,
  deleteCourseRequestState: RequestState.FAILED,
});

export const handleDeleteCourseSuccess = (state: ICoursesState, action: IAction): ICoursesState => ({
  ...state,
  courses: action.courses,
  deleteCourseRequestError: null,
  deleteCourseRequestState: RequestState.SUCCEEDED,
});

export default combineReducers<IState, Action>({
  _: createReducer<ICoursesState>(INITIAL_STATE, {
    [Types.MOUNT]: mount,
    [Types.CREATE_COURSE]: createCourse,
    [Types.HANDLE_CREATE_COURSE_FAILURE]: handleCreateCourseFailure,
    [Types.HANDLE_CREATE_COURSE_SUCCESS]: handleCreateCourseSuccess,
    [Types.DELETE_COURSE]: deleteCourse,
    [Types.HANDLE_DELETE_COURSE_FAILURE]: handleDeleteCourseFailure,
    [Types.HANDLE_DELETE_COURSE_SUCCESS]: handleDeleteCourseSuccess,
    [Types.FETCH_ALL_COURSES]: fetchAllCourses,
    [Types.HANDLE_FETCH_ALL_COURSES_FAILURE]: handleFetchAllCoursesFailure,
    [Types.HANDLE_FETCH_ALL_COURSES_SUCCESS]: handleFetchAllCoursesSuccess,
    [Types.FETCH_ALL_SCORM_PACKAGES]: fetchAllScormPackages,
    [Types.HANDLE_FETCH_ALL_SCORM_PACKAGES_SUCCESS]: handleFetchAllScormPackagesSuccess,
    [Types.HANDLE_FETCH_ALL_SCORM_PACKAGES_FAILURE]: handleFetchAllScormPackagesFailure,
    [Types.UNMOUNT]: unmount,
  }),
});
